/**
 * Spaces and their teams on screen (RESTORE_PLAN R1.5). From the pre-November ProjectDialog,
 * ProjectsList, ProjectDetailView, TeamDialog and TeamsList: the space types (with Jira Product
 * Discovery), each team's description and request types, the readiness line, Save only when
 * changed. The server rules are in supabase/functions/server/spaceTeamsCore.ts.
 */

export type SpaceType = 'jsm' | 'jira' | 'confluence' | 'product-discovery';

export const SPACE_TYPE_OPTIONS: { value: SpaceType; label: string; short: string; description: string; badgeClass: string }[] = [
  {
    value: 'jsm',
    label: 'Jira Service Management (JSM)',
    short: 'JSM',
    description: 'For service desks, IT support, HR services and customer support, with SLAs and request management',
    badgeClass: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  {
    value: 'jira',
    label: 'Jira Software',
    short: 'Jira',
    description: 'For software teams using agile boards, sprints and issue tracking',
    badgeClass: 'bg-purple-100 text-purple-700 border-purple-200',
  },
  {
    value: 'product-discovery',
    label: 'Jira Product Discovery',
    short: 'Product Discovery',
    description: 'For product teams to capture, organize and prioritize ideas and opportunities',
    badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  {
    value: 'confluence',
    label: 'Confluence',
    short: 'Confluence',
    description: 'For knowledge bases, runbooks and documentation linked to the service desk',
    badgeClass: 'bg-green-100 text-green-700 border-green-200',
  },
];

export function spaceTypeOption(type: string | undefined) {
  return SPACE_TYPE_OPTIONS.find((o) => o.value === type);
}

export function spaceTypeLabel(type: string | undefined, form: 'short' | 'long' = 'short'): string {
  const o = spaceTypeOption(type);
  if (!o) return type || '';
  return form === 'long' ? o.label : o.short;
}

export function spaceTypeBadgeClass(type: string | undefined): string {
  return spaceTypeOption(type)?.badgeClass || 'bg-gray-100 text-gray-700 border-gray-200';
}

export interface TeamDetail {
  name: string;
  description: string;
}

export interface SpaceTeamsShape {
  teams?: unknown;
  teamDetails?: unknown;
}

/** A space's teams with their descriptions; reads `teams` as names or as `{ name, description }`. */
export function teamDetailsOf(space: SpaceTeamsShape | null | undefined): TeamDetail[] {
  if (!space) return [];
  const described = new Map<string, string>();
  if (Array.isArray(space.teamDetails)) {
    for (const d of space.teamDetails as TeamDetail[]) {
      if (d && typeof d.name === 'string') described.set(d.name.trim().toLowerCase(), String(d.description ?? ''));
    }
  }
  const out: TeamDetail[] = [];
  const seen = new Set<string>();
  for (const t of Array.isArray(space.teams) ? space.teams : []) {
    const name = String(typeof t === 'object' && t ? (t as TeamDetail).name : t ?? '').trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    const own = typeof t === 'object' && t ? (t as TeamDetail).description : undefined;
    out.push({ name, description: String(own ?? described.get(name.toLowerCase()) ?? '') });
  }
  return out;
}

export interface TeamRequestTypeRef {
  id: string;
  requestTypeName?: string;
  status?: string;
  team?: string;
  submitterAccess?: string;
  allowedTeams?: string[];
  fields?: unknown[];
  serviceProjectId?: string;
  [k: string]: unknown;
}

/** The request types a team owns (its count, as the old team cards showed), and the ones restricted to it. */
export function requestTypesForTeam<T extends TeamRequestTypeRef>(name: string, requestTypes: T[]): { owned: T[]; restricted: T[] } {
  const n = name.trim().toLowerCase();
  return {
    owned: requestTypes.filter((rt) => String(rt.team ?? '').trim().toLowerCase() === n),
    restricted: requestTypes.filter(
      (rt) => rt.submitterAccess === 'specific_teams'
        && String(rt.team ?? '').trim().toLowerCase() !== n
        && (rt.allowedTeams || []).some((t) => t.trim().toLowerCase() === n),
    ),
  };
}

export function requestTypeCountLabel(count: number): string {
  return `${count} request ${count === 1 ? 'type' : 'types'}`;
}

export interface SpaceReadiness {
  applicable: boolean;
  teams: number;
  workflows: number;
  workTypes: number;
  ready: boolean;
  missing: string[];
}

/** "Space ready for implementation", or what it still needs ("Needs teams and work types"). */
export function readinessText(r: SpaceReadiness | null | undefined): string | null {
  if (!r || !r.applicable) return null;
  if (r.ready) return 'Space ready for implementation';
  const m = r.missing;
  const list = m.length <= 1 ? m.join('') : `${m.slice(0, -1).join(', ')} and ${m[m.length - 1]}`;
  return `Needs ${list}`;
}

/**
 * A space's config items: those in the per-item store (`config_item:{companyId}:*`, which the client
 * page's space cards and the readiness line count) that name the space, then any from the company's
 * older list (`GET /companies/:id/config-items`) that name it and are not already there.
 */
export function itemsInSpace<T extends { id?: string; spaceId?: string }>(spaceId: string, perItem: T[], older: T[]): T[] {
  const own = (perItem || []).filter((i) => i && i.spaceId === spaceId);
  const ids = new Set(own.map((i) => i.id));
  return [...own, ...(older || []).filter((i) => i && i.spaceId === spaceId && !ids.has(i.id))];
}

/** The fields the space editor owns (ProjectDialog), compared after trimming; key in upper case. */
export interface SpaceEditDraft {
  name: string;
  description: string;
  type: SpaceType;
  key: string;
  jiraSpaceUrl: string;
}

export function spaceDraftFrom(space: Partial<SpaceEditDraft> & { type?: string } | null | undefined): SpaceEditDraft {
  return {
    name: String(space?.name ?? ''),
    description: String(space?.description ?? ''),
    type: (spaceTypeOption(space?.type)?.value || 'jsm') as SpaceType,
    key: String(space?.key ?? ''),
    jiraSpaceUrl: String(space?.jiraSpaceUrl ?? ''),
  };
}

/** Only the fields that changed (empty when the edit changed nothing, so Save stays off). */
export function changedSpaceDraft(original: SpaceEditDraft, draft: SpaceEditDraft): Partial<SpaceEditDraft> {
  const out: Partial<SpaceEditDraft> = {};
  if (draft.name.trim() !== original.name.trim()) out.name = draft.name.trim();
  if (draft.description.trim() !== original.description.trim()) out.description = draft.description.trim();
  if (draft.type !== original.type) out.type = draft.type;
  if (draft.key.trim().toUpperCase() !== original.key.trim().toUpperCase()) out.key = draft.key.trim().toUpperCase();
  if (draft.jiraSpaceUrl.trim() !== original.jiraSpaceUrl.trim()) out.jiraSpaceUrl = draft.jiraSpaceUrl.trim();
  return out;
}

/** The same key rule as the create dialog and the server: 2 to 10 upper-case letters or digits. */
export function spaceKeyProblem(key: string): string | null {
  const k = key.trim().toUpperCase();
  if (!k) return 'A space key is required';
  if (!/^[A-Z0-9]+$/.test(k)) return 'Only uppercase letters and numbers are allowed';
  if (k.length < 2 || k.length > 10) return 'Space key must be 2–10 characters';
  return null;
}

/** An empty link is fine; otherwise it must be a web address (the server adds https:// to a bare host). */
export function jiraSpaceUrlProblem(url: string): string | null {
  const v = url.trim();
  if (!v) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return 'The Jira space URL must start with https://';
    if (!parsed.hostname.includes('.')) return 'Enter the full address, such as https://yourcompany.atlassian.net/…';
    return null;
  } catch {
    return 'The Jira space URL is not a valid address';
  }
}

/** The link to open (a bare host gets https://). */
export function jiraSpaceHref(url: string | undefined): string | null {
  const v = String(url ?? '').trim();
  if (!v || jiraSpaceUrlProblem(v)) return null;
  return /^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`;
}

/** Whether a team name is taken on the space (case-insensitive), ignoring the team being edited. */
export function teamNameTaken(teams: TeamDetail[], name: string, editing?: string): boolean {
  const n = name.trim().toLowerCase();
  const e = editing?.trim().toLowerCase();
  return teams.some((t) => t.name.toLowerCase() === n && t.name.toLowerCase() !== e);
}
